import { Note } from '@card-software/library';
import { AuditTrail } from '@card-software/nexus-models';
import { WorkOrder } from '../order/work-order';
import { ProductionCard } from '../production/production-card';
import { Roll } from '../production/roll';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ResponseTranslation } from '../translation/Translations';

export enum DispatchInstructionStatus {
  Draft = 0,
  SentToWarehouse = 1,
  InProgress = 2,
  Completed = 3,
  Cancelled = 4,
}

export enum DispatchInstructionType {
  Standard = 0,
  BGrade = 1,
  GreigeOnly = 2,
  Chemical = 3,
}

export interface BatchEntryLotItemKey {
  lotItemNumber: string;
  quantity: number;
}

export interface ChemicalDispatchInstructionLine {
  lineId: string;
  itemCode: string;
  productDevelopmentNumber?: string;
  clientPurchaseOrderReference?: string;
  clientPurchaseOrderNumber?: string;
  lotItems: BatchEntryLotItemKey[];
}

export interface ChemicalDispatchLotItemRow {
  lotItemNumber: string;
  quantity: number;
  locationCode?: string;
  manufacturerLotNumber?: string;
}

export interface ChemicalDispatchLotItemBatchGroup {
  batchNumber: string | null;
  items: ChemicalDispatchLotItemRow[];
  totalQuantity: number;
}

export interface AddChemicalLotItemCommand {
  itemCode: string;
  clientPurchaseOrderReference?: string;
  clientPurchaseOrderNumber?: string;
  lotItemNumber: string;
}

export interface AddChemicalLineCommand {
  itemCode: string;
  clientPurchaseOrderReference?: string;
  clientPurchaseOrderNumber?: string;
}

export interface ScannedRoll {
  roll?: Roll;
  rollNumber: string;
  scannedAt?: Date;
  scannedBy?: string;
}

export interface FabricAndBalance {
  fabricCode: string;
  scannedRolls: ScannedRoll[];
}

export interface DispatchInstructionLine {
  lineId: string;
  workOrderNumber: string;
  clientProductOrderNumber: string;
  colorReference: string;
  workOrderReference: string;
  colorName?: string;
  fabricColor?: string;
  fabricsByColor?: Record<string, string[]>;
  fabricsAndBalances: FabricAndBalance[];
  workOrder?: WorkOrder;
  productionCards: ProductionCard[];
  otherDispatchInstructionsWithRolls?: OtherDispatchInstructionInfo[];
}

export interface OtherDispatchInstructionInfo {
  status: DispatchInstructionStatus;
  dispatchInstructionNumber: string;
  dispatchInstructionReference: string;
  rolls: Roll[];
  totalWeightInLbs: number;
  totalWeightInKg: number;
  totalLengthInYards: number;
}

export interface DateAndPriorityKey {
  priority: number;
  date: string;
}

export interface FabricCustomizationSplit {
  rollNumbers: string[];
  unitPrice?: number;
}

export interface FabricCustomization {
  fabricCode: string;
  colorReference?: string;
  productionCardNumber?: string;
  aliasReference?: string;
  splits: FabricCustomizationSplit[];
}

/**
 * Completion progress for a given Fabric + Color, aggregated across all lines that share that
 * combo, measured against the Work Order's GarmentDetails.QuantityInPounds target (includes
 * weight already dispatched on other non-cancelled dispatch instructions). Populated by the API.
 */
export interface FabricColorCompletion {
  fabricCode: string;
  colorReference: string;
  requestedQuantityInPounds: number;
  currentDispatchWeightInPounds: number;
  otherDispatchInstructionsWeightInPounds: number;
  totalDispatchedWeightInPounds: number;
  completionPercentage: number;
}

export interface DispatchInstruction extends AuditTrail {
  id: string;
  number: string;
  type: DispatchInstructionType;
  bGradeWorkOrderReference?: string;
  greigeOnlyWorkOrderReference?: string;
  voyageId?: string;
  booking?: string;
  invoiceNumber?: string;
  status: DispatchInstructionStatus;
  clientReference: string;
  clientName?: string;
  clientPOs: string[];
  shippingToReference?: string;
  shippingToName?: string;
  shipToAddressCode: string;
  shippingAddress?: string;
  consigneeReference?: string;
  consigneeName?: string;
  consigneeAddressCode?: string;
  consigneeAddress?: string;
  shippingToRepresentativeReference?: string;
  shippingToRepresentativeName?: string;
  billToReference?: string;
  billToName?: string;
  billToAddressCode?: string;
  billingAddress?: string;
  invoiceComment?: string;
  notes: Note[];
  lines: DispatchInstructionLine[];
  chemicalLines: ChemicalDispatchInstructionLine[];
  unsentRolls: string[];
  dateAndPriorityKey: DateAndPriorityKey;
  fabricCustomizations?: FabricCustomization[];
  extraTolerancePercentage?: number;
  extraToleranceComment?: string;
  fabricColorCompletions?: FabricColorCompletion[];
}

export interface DispatchInstructionInsert {
  type: DispatchInstructionType;
  bGradeWorkOrderReference?: string;
  greigeOnlyWorkOrderReference?: string;
  status: DispatchInstructionStatus;
  clientReference: string;
  shippingToReference?: string;
  shipToAddressCode: string;
  billToReference?: string;
  billToAddressCode?: string;
  destinationLocationCode?: string;
  notes: Note[];
  lines: DispatchInstructionLine[];
}
